import { generatePath, useNavigate } from 'react-router-dom';
import { CopyIcon, NotePencilIcon, TrashIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { ActionBar } from '@/components';
import { AppRoutes } from '@/constants/routes';
import { FullAlertRule } from '@/types';
import { Permission } from '@/types/permissions';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { ActionConfig } from '@/components/ActionBar';
import { ActionType } from '@/components/ActionBar';

interface AlertRuleActionBarProps {
    alertRule: FullAlertRule;
    onDelete?: () => void;
    isDeleting?: boolean;
}

export function AlertRuleActionBar({
    alertRule,
    onDelete,
    isDeleting,
}: AlertRuleActionBarProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const navigate = useNavigate();

    const handleEdit = () => {
        const editPath = generatePath(AppRoutes.ALERT_RULES_EDIT, {
            id: alertRule.id,
        });
        navigate(editPath);
    };

    const handleDuplicate = () => {
        navigate(AppRoutes.ALERT_RULES_CREATE, {
            state: {
                duplicateFrom: {
                    ...alertRule,
                    name: `${alertRule.name} (Copy)`,
                },
            },
        });
    };

    const actions: ActionConfig[] = [
        {
            type: ActionType.ITEM,
            icon: NotePencilIcon,
            label: t('actionBar.edit'),
            onClick: handleEdit,
            permission: Permission.EDIT,
        },
        {
            type: ActionType.ITEM,
            icon: CopyIcon,
            label: t('actionBar.duplicate'),
            onClick: handleDuplicate,
            permission: Permission.DUPLICATE,
        },
        {
            type: ActionType.ITEM,
            icon: TrashIcon,
            label: isDeleting ? t('actionBar.deleting') : t('actionBar.delete'),
            onClick: () => onDelete?.(),
            permission: Permission.DELETE,
            disabled: isDeleting,
        },
    ];

    return <ActionBar actions={actions} />;
}
